import { useState, useCallback, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, useMotionValue, useSpring, animate } from 'framer-motion'
import { Check, ArrowRight } from 'lucide-react'
import { useSliderKeyboard } from '@/hooks/useSliderKeyboard'
import SliderSwipePrompt from '@/components/SliderSwipePrompt'

export interface PricingPackage {
  name: string
  price: string
  period: string
  description: string
  features: string[]
  cta: string
  popular: boolean
}

interface PricingTableProps {
  packages: PricingPackage[]
}

const TILT_MAX = 5

function PricingCard({ pkg, preselectedService, disableTilt }: { pkg: PricingPackage; preselectedService?: string; disableTilt?: boolean }) {
  const ref = useRef<HTMLDivElement>(null)
  const tiltX = useMotionValue(0)
  const tiltY = useMotionValue(0)
  const smoothTiltX = useSpring(tiltX, { stiffness: 400, damping: 30 })
  const smoothTiltY = useSpring(tiltY, { stiffness: 400, damping: 30 })

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (disableTilt) return
    const rect = ref.current?.getBoundingClientRect()
    if (!rect || rect.width === 0 || rect.height === 0) return
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2
    tiltX.set(x * TILT_MAX)
    tiltY.set(-y * TILT_MAX)
  }, [disableTilt, tiltX, tiltY])

  const handleMouseLeave = useCallback(() => {
    tiltX.set(0)
    tiltY.set(0)
  }, [tiltX, tiltY])

  const ctaHref = preselectedService
    ? `/new-project?service=${encodeURIComponent(preselectedService)}`
    : '/new-project'

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative rounded-2xl p-8 md:p-10 flex flex-col bg-white/5 border border-white/10 hover:border-white/20 h-full perspective-1000"
      style={{
        rotateX: disableTilt ? 0 : smoothTiltY,
        rotateY: disableTilt ? 0 : smoothTiltX,
        transformPerspective: 1000,
        willChange: disableTilt ? 'auto' : 'transform',
      }}
    >
      {pkg.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-white text-accent text-xs font-bold uppercase tracking-wider rounded-full">
          Recommended
        </div>
      )}
      <h4 className="text-xl font-bold font-heading text-white uppercase tracking-tight mb-2">
        {pkg.name}
      </h4>
      <div className="flex items-baseline gap-1 mb-4">
        <span className="text-4xl md:text-5xl font-bold font-heading text-white">{pkg.price}</span>
        {pkg.period && <span className="text-slate-400">{pkg.period}</span>}
      </div>
      <p className="text-slate-400 text-sm mb-8 min-h-[2.5rem]">
        {pkg.description}
      </p>
      <ul className="space-y-4 mb-10 flex-1">
        {pkg.features.map((feature) => (
          <li key={feature} className="flex items-start gap-3 text-slate-300">
            <Check className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      <Link
        to={ctaHref}
        className="inline-flex items-center justify-center gap-2 w-full py-4 rounded-full font-bold uppercase tracking-wider transition-all duration-300 bg-white/10 text-white border border-white/20 hover:bg-white/20 hover:border-white/30"
      >
        {pkg.cta}
        <ArrowRight className="h-4 w-4" />
      </Link>
    </motion.div>
  )
}

export default function PricingTable({ packages }: PricingTableProps) {
  const location = useLocation()
  const preselectedService = (() => {
    const path = location.pathname.toLowerCase()
    if (path.includes('/growth') || path.includes('/tradies')) return 'Growth'
    if (path.includes('/strategy')) return 'Strategy'
    if (path.includes('/design')) return 'Design'
    if (path.includes('/engineering')) return 'Engineering'
    if (path.includes('/insights')) return 'Insights'
    if (path.includes('/automation')) return 'Automation'
    if (path.includes('/authority')) return 'Authority'
    return undefined
  })()

  const popularIndex = packages.findIndex((p) => p.popular)
  const initialSlide = popularIndex >= 0 ? popularIndex : 1

  const [currentSlide, setCurrentSlide] = useState(initialSlide)
  const [containerWidth, setContainerWidth] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const x = useMotionValue(0)
  const isDraggingRef = useRef(false)

  // 1 slide on mobile (<768px), 2 slides at md/lg (768-1279px), grid at xl (1280+)
  const slidesPerView = containerWidth > 0 && containerWidth < 768 ? 1 : containerWidth < 1280 ? 2 : 1
  const gap = 16
  const perSlideWidth = containerWidth > 0 ? (containerWidth - (slidesPerView - 1) * gap) / slidesPerView : 0

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? container.offsetWidth
      setContainerWidth(width)
    })
    observer.observe(container)
    setContainerWidth(container.offsetWidth)
    return () => observer.disconnect()
  }, [])

  const stepSize = slidesPerView === 1 ? perSlideWidth : perSlideWidth + gap
  const maxSlide = Math.max(0, packages.length - slidesPerView)
  const clampedInitialSlide = slidesPerView === 2 ? 0 : Math.min(initialSlide, maxSlide)

  useEffect(() => {
    if (stepSize > 0) {
      x.set(-clampedInitialSlide * stepSize)
      setCurrentSlide(clampedInitialSlide)
    }
  }, [clampedInitialSlide, stepSize, x])

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(Math.max(0, Math.min(index, maxSlide)))
  }, [maxSlide])

  const goPrev = useCallback(() => {
    goToSlide(currentSlide - 1)
  }, [currentSlide, goToSlide])

  const goNext = useCallback(() => {
    goToSlide(currentSlide + 1)
  }, [currentSlide, goToSlide])

  useEffect(() => {
    setCurrentSlide((prev) => Math.min(prev, maxSlide))
  }, [maxSlide])

  useEffect(() => {
    if (!isDraggingRef.current && stepSize > 0) {
      animate(x, -currentSlide * stepSize, { type: 'spring', stiffness: 300, damping: 30 })
    }
  }, [currentSlide, stepSize, x])

  const handleDragStart = useCallback(() => {
    isDraggingRef.current = true
  }, [])

  const handleDragEnd = useCallback((_: MouseEvent | TouchEvent | PointerEvent, info: { offset: { x: number }; velocity: { x: number } }) => {
    isDraggingRef.current = false
    if (stepSize === 0) return
    const threshold = stepSize * 0.15
    const velocityThreshold = 500
    const shouldGoPrev = info.offset.x > threshold || info.velocity.x > velocityThreshold
    const shouldGoNext = info.offset.x < -threshold || info.velocity.x < -velocityThreshold
    if (shouldGoPrev) goToSlide(currentSlide - 1)
    else if (shouldGoNext) goToSlide(currentSlide + 1)
  }, [currentSlide, goToSlide, stepSize])

  useSliderKeyboard(containerRef, goPrev, goNext, true)

  return (
    <>
      {/* Slider: mobile (1 slide) and md/lg (2 slides) */}
      <div ref={containerRef} className="xl:hidden w-full overflow-hidden">
        <SliderSwipePrompt>
        <div className="relative overflow-hidden cursor-grab active:cursor-grabbing pt-8">
          <motion.div
            className={`relative z-10 flex ${slidesPerView === 2 ? 'gap-4' : ''}`}
            style={{ x }}
            drag="x"
            dragConstraints={stepSize > 0 ? { left: -maxSlide * stepSize, right: 0 } : undefined}
            dragElastic={0.15}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            whileTap={{ cursor: 'grabbing' }}
          >
            {packages.map((pkg) => (
              <div
                key={pkg.name}
                className="flex-shrink-0 px-2"
                style={{ minWidth: perSlideWidth > 0 ? perSlideWidth : '100%', width: perSlideWidth > 0 ? perSlideWidth : undefined }}
              >
                <PricingCard pkg={pkg} preselectedService={preselectedService} disableTilt />
              </div>
            ))}
          </motion.div>
        </div>
        </SliderSwipePrompt>
        <div className="flex items-center justify-center gap-2 mt-6">
          {Array.from({ length: maxSlide + 1 }).map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'bg-accent w-6' : 'bg-white/30 w-1.5 hover:bg-white/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Grid: xl only */}
      <div className="hidden xl:grid xl:grid-cols-3 gap-6 lg:gap-8 pt-6">
        {packages.map((pkg, i) => (
          <motion.div
            key={pkg.name}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
          >
            <PricingCard pkg={pkg} preselectedService={preselectedService} />
          </motion.div>
        ))}
      </div>
    </>
  )
}
